import { useContext, useEffect, useRef } from 'react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { UserContext } from './userContext';

function useUserSession() {
  const { loading, setLoading, nbnotif, setNbnotif, user, setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const initialized = useRef(false);

  useEffect(() => {
    // Empêche les exécutions multiples (App + ProtectedRoute appellent tous deux ce hook)
    if (initialized.current) return;
    initialized.current = true;

    const auth = getAuth();

    // Restaure les données utilisateur depuis le localStorage
    const localToken = localStorage.getItem('token');
    const localUid = localStorage.getItem('uid');
    const localName = localStorage.getItem('name');
    const localEmail = localStorage.getItem('email');
    const localPhotoURL = localStorage.getItem('photoURL');

    if (localToken && localUid && localName && localEmail) {
      setUser({
        uid: localUid,
        name: localName,
        email: localEmail,
        token: localToken,
        photoURL: localPhotoURL || null,
      });
    }

    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      const token = localStorage.getItem('token');

      if (authUser) {
        if (!token) {
          await signOut(auth);
          localStorage.clear();
          navigate('/login');
        }
      } else {
        if (!token) {
          localStorage.clear();
          navigate('/login');
        }
      }

      setLoading(false);
    });

    // Sécurité : si onAuthStateChanged ne se déclenche pas après 3s, débloquer le chargement
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 3000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Déconnexion manuelle (Firebase ou autre)
  const logout = async () => {
    const auth = getAuth();
    const token = localStorage.getItem('token');

    try {
      if (token) {
        // Cas où l'utilisateur est connecté avec Firebase (Google)
        const isFirebaseAuth = auth.currentUser !== null;

        if (isFirebaseAuth) {
          await signOut(auth); // Déconnecte de Firebase si connecté avec Google
        }
      }

      // Dans tous les cas, nettoyage du localStorage
      localStorage.clear();
      setUser(null); // Réinitialise le contexte utilisateur
      navigate('/login'); // Redirige vers la page de connexion
    } catch (error) {
      console.error('Erreur lors de la déconnexion :', error);
    }
  };

  return { loading, nbnotif, setNbnotif, logout, user, setUser };
}

export default useUserSession;
